/* ============================================================
   ProviderProfilePanel — registration form + edit profile
   ============================================================ */

import { useState } from 'react';
import { api, resolveMediaUrl } from '../api/client';
import type { ProviderProfileDetail, RegisterProvider, UpdateProviderProfile } from '../types/provider';

interface Props {
  mode: 'register' | 'edit';
  profile?: ProviderProfileDetail | null;
  onRegistered?: () => void;
  onUpdated?: () => void;
}

export function ProviderProfilePanel({ mode, profile, onRegistered, onUpdated }: Props) {
  const [companyName, setCompanyName] = useState(profile?.companyName ?? '');
  const [bio, setBio] = useState(profile?.bio ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(profile?.websiteUrl ?? '');
  const [logoUrl, setLogoUrl] = useState(profile?.logoUrl ?? '');
  const [logoUploading, setLogoUploading] = useState(false);
  const [isAvailable, setIsAvailable] = useState(profile?.isAvailable ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ME-3: providers upload the logo as an image — the engine hosts it.
  const uploadLogo = async (file: File | null) => {
    if (!file) return;
    setLogoUploading(true);
    setError(null);
    try {
      const uploaded = await api.uploadMedia(file);
      if (uploaded.kind !== 'image') {
        throw new Error('Логотип должен быть картинкой (PNG/JPEG/WebP).');
      }
      setLogoUrl(uploaded.thumbnailUrl ?? uploaded.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось загрузить логотип');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (mode === 'register') {
        const dto: RegisterProvider = { companyName, bio: bio || undefined, websiteUrl: websiteUrl || undefined };
        await api.registerProvider(dto);
        onRegistered?.();
      } else {
        const dto: UpdateProviderProfile = {
          companyName, bio: bio || null, logoUrl: logoUrl || null,
          websiteUrl: websiteUrl || null, isAvailable,
        };
        await api.updateProviderProfile(dto);
        onUpdated?.();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ошибка сохранения');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)', maxWidth: 560 }}>
      <div>
        <h2 style={{ fontSize: 'var(--fs-h2)', fontWeight: 'var(--fw-bold)' }}>
          {mode === 'register' ? 'Регистрация исполнителя' : 'Профиль компании'}
        </h2>
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-xs)' }}>
          {mode === 'register'
            ? 'Заполните данные о вашей компании, чтобы начать принимать заказы.'
            : 'Обновите информацию о компании.'}
        </p>
      </div>

      <Field label="Название компании" required>
        <input
          type="text"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          required
          placeholder="ООО «3D Печать»"
          style={inputStyle}
        />
      </Field>

      <Field label="Описание">
        <textarea
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          rows={3}
          placeholder="Расскажите о своих услугах, оборудовании, опыте..."
          style={{ ...inputStyle, resize: 'vertical', minHeight: 80 }}
        />
      </Field>

      <Field label="Сайт / соцсети">
        <input
          type="url"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          placeholder="https://example.com"
          style={inputStyle}
        />
      </Field>

      {mode === 'edit' && (
        <>
          <Field label="Логотип (PNG/JPEG/WebP, до 10 МБ)">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
              {logoUrl ? (
                <img
                  src={resolveMediaUrl(logoUrl)}
                  alt="Логотип"
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 'var(--radius-md)',
                    objectFit: 'cover',
                    border: '1px solid var(--color-separator)',
                  }}
                />
              ) : (
                <div
                  aria-hidden
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 'var(--radius-md)',
                    border: '1px dashed var(--color-separator)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'var(--color-text-tertiary)',
                    fontSize: 22,
                    fontWeight: 'var(--fw-bold)',
                  }}
                >
                  {companyName.slice(0, 1).toUpperCase()}
                </div>
              )}
              <div style={{ display: 'flex', gap: 'var(--space-sm)', alignItems: 'center' }}>
                <label className="btn btn-secondary" style={{ height: 36, padding: '0 var(--space-md)', cursor: logoUploading ? 'wait' : 'pointer' }}>
                  {logoUploading ? 'Загрузка…' : logoUrl ? 'Заменить' : 'Загрузить'}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    disabled={logoUploading}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      void uploadLogo(e.target.files?.[0] ?? null);
                      e.target.value = '';
                    }}
                  />
                </label>
                {logoUrl && (
                  <button
                    type="button"
                    className="btn btn-secondary"
                    style={{ height: 36, padding: '0 var(--space-md)' }}
                    onClick={() => setLogoUrl('')}
                  >
                    Удалить
                  </button>
                )}
              </div>
            </div>
          </Field>

          {/* Availability toggle */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: 'var(--space-md) 0',
            }}
          >
            <div>
              <p style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-semibold)' }}>Доступен для заказов</p>
              <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                Включите, чтобы получать новые заказы
              </p>
            </div>
            <ToggleSwitch checked={isAvailable} onChange={setIsAvailable} />
          </div>

          {profile && (
            <div style={{ display: 'flex', gap: 'var(--space-md)', flexWrap: 'wrap' }}>
              <Stat label="Рейтинг" value={profile.rating ? `${profile.rating.toFixed(1)} ★` : '—'} />
              <Stat label="Заказов" value={String(profile.completedOrderCount)} />
              <Stat label="Отклик" value={profile.averageResponseHours ? `${profile.averageResponseHours.toFixed(0)}ч` : '—'} />
            </div>
          )}
        </>
      )}

      {error && (
        <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-danger)' }}>{error}</p>
      )}

      <button type="submit" className="btn btn-primary" disabled={saving || !companyName.trim()}>
        {saving ? 'Сохранение...' : mode === 'register' ? 'Стать исполнителем' : 'Сохранить'}
      </button>
    </form>
  );
}

/* ── Reusable field wrapper ──────────────────────────────── */

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
      <span style={{ fontSize: 'var(--fs-caption)', fontWeight: 'var(--fw-semibold)', color: 'var(--color-text-secondary)' }}>
        {label}{required && ' *'}
      </span>
      {children}
    </label>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div
      className="card"
      style={{
        flex: '1 1 100px',
        textAlign: 'center',
        padding: 'var(--space-md)',
        border: '1px solid var(--color-separator)',
      }}
    >
      <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 'var(--fw-bold)' }}>{value}</p>
      <p style={{ fontSize: 'var(--fs-overline)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </p>
    </div>
  );
}

/* ── iOS Toggle Switch ───────────────────────────────────── */

function ToggleSwitch({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      style={{
        width: 51,
        height: 31,
        borderRadius: 'var(--radius-pill)',
        background: checked ? 'var(--color-success)' : 'var(--color-text-tertiary)',
        position: 'relative',
        transition: 'background var(--transition-fast)',
        flexShrink: 0,
        cursor: 'pointer',
        border: 'none',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 22 : 2,
          width: 27,
          height: 27,
          borderRadius: '50%',
          background: '#FFFFFF',
          boxShadow: '0 1px 3px rgba(0,0,0,0.15)',
          transition: 'left var(--transition-fast)',
        }}
      />
    </button>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 48,
  padding: '12px 16px',
  fontSize: 'var(--fs-body)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  background: 'var(--color-surface)',
  color: 'var(--color-text-primary)',
  outline: 'none',
  transition: 'border-color var(--transition-fast)',
};
